/**
 * bot-status の判定結果（`bot-status-health.ts` の種別 + 数値）を表示文言に変換する。
 *
 * 判定は言語非依存の純関数に閉じ、文言はここで i18next の t() を通す。判定の境界テストと
 * 翻訳の追加を互いに巻き込まないための分離。
 *
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business bot 稼働状況の健全性判定結果を ja / en / vi の表示文言に変換する
 * @graph-connects toDuration [calls] 経過時間の単位丸め
 */
import type { TFunction } from 'i18next';
import { toDuration, type HealthReason, type HostWarning } from './bot-status-health.js';

/**
 * 経過ミリ秒を「3 分」「2 時間」等の文言にする。
 * @param t 翻訳関数
 * @param ms 経過ミリ秒
 * @returns 単位付きの経過時間
 * @graph-connects none
 */
export function describeDuration(t: TFunction, ms: number): string {
  const duration = toDuration(ms);
  if (duration.unit === 'minutes')
    return t('botStatus.duration.minutes', { count: duration.count });
  if (duration.unit === 'hours') return t('botStatus.duration.hours', { count: duration.count });
  return t('botStatus.duration.days', { count: duration.count });
}

/**
 * 健全性判定の理由を文言にする。
 * @param t 翻訳関数
 * @param reason 判定理由
 * @returns 表示文言
 * @graph-connects none
 */
export function describeReason(t: TFunction, reason: HealthReason): string {
  switch (reason.kind) {
    case 'noReport':
      return t('botStatus.reason.noReport');
    case 'invalidTime':
      return t('botStatus.reason.invalidTime');
    case 'stale':
      return t('botStatus.reason.stale', { duration: describeDuration(t, reason.ageMs) });
    case 'fresh':
      return t('botStatus.reason.fresh');
    case 'stopped':
      return reason.detail ?? t('botStatus.reason.stopped');
    case 'failures':
      return reason.window
        ? t('botStatus.reason.failuresInWindow', { count: reason.count, window: reason.window })
        : t('botStatus.reason.failures', { count: reason.count });
    case 'idle':
      return t('botStatus.reason.idle', { duration: describeDuration(t, reason.idleMs) });
    case 'healthy':
      return t('botStatus.reason.healthy');
    case 'servicesDown':
      return t('botStatus.reason.servicesDown', { count: reason.count });
    case 'warnings':
      return t('botStatus.reason.warnings', { count: reason.count });
    case 'allHealthy':
      return t('botStatus.reason.allHealthy');
  }
}

/**
 * ホスト状態の警告を文言にする。
 * @param t 翻訳関数
 * @param warning 警告
 * @returns 表示文言
 * @graph-connects none
 */
export function describeHostWarning(t: TFunction, warning: HostWarning): string {
  switch (warning.kind) {
    case 'backupUnmeasured':
      return t('botStatus.warning.backupUnmeasured', {
        source: warning.source ?? t('botStatus.unknownSource'),
      });
    case 'backupStale':
      return t('botStatus.warning.backupStale', { hours: warning.hours });
    case 'swapHigh':
      return t('botStatus.warning.swapHigh', { percent: warning.percent });
    case 'memoryLow':
      return t('botStatus.warning.memoryLow', { percent: warning.percent });
    case 'diskLow':
      return t('botStatus.warning.diskLow', { gb: warning.gb });
  }
}
